import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { Easel } from '../../../../_models/easel';
import { EaselType } from '../../../../_models/easel_type';
import { EaselFamily } from '../../../../_models/easel_family';
import { EaselService } from '../../easel.service';

@Component({
  selector: 'app-easel',
  templateUrl: './easel-list.component.html',
  styleUrls: ['../../../master-page.css']
})
export class EaselListComponent implements OnInit {
  searchForm: FormGroup;
  editorForm: FormGroup;
  rows: Easel[] = [];
  types: EaselType[] = [];
  families: EaselFamily[] = [];
  selected: Easel = null;
  loading = false;
  saving = false;
  deleteModal = false;
  errorMessage = '';
  feedbackMessage = '';

  constructor(private formBuilder: FormBuilder, private easelService: EaselService) {}

  ngOnInit(): void {
    this.searchForm = this.formBuilder.group({ f_type: [3], f_name: [''], f_desc: [''], f_active: [false] });
    this.editorForm = this.formBuilder.group({
      id: [null], name: ['', Validators.required], desc: [''], type: [3, Validators.required], family: [null, Validators.required],
      sides: [1, Validators.min(1)], stacks: [1, Validators.min(1)], width: [0, Validators.required], height: [0, Validators.required],
      depth: [0, Validators.required], weight: [0], max_width: [0], max_height: [0], thickness: [0], max_weight: [0],
      separator: [0], stack_distance: [0], priority: [999], safety_margin: [0], cost_m3: [0], active: [true]
    });
    this.loadCatalogs();
    this.loadRows();
  }

  loadCatalogs(): void {
    this.easelService.getTypes().subscribe(data => this.types = data || []);
    this.easelService.getFamilies().subscribe(data => this.families = data || []);
  }

  loadRows(): void {
    this.loading = true;
    this.errorMessage = '';
    this.easelService.getEasels(this.searchForm.value).subscribe(data => { this.rows = data || []; this.loading = false; });
  }

  clearFilters(): void { this.searchForm.reset({ f_type: 3, f_name: '', f_desc: '', f_active: false }); this.loadRows(); }

  createNew(): void {
    this.selected = null;
    this.errorMessage = '';
    this.feedbackMessage = '';
    this.editorForm.reset({ id: null, name: '', desc: '', type: 3, family: this.defaultFamily(), sides: 1, stacks: 1, width: 0, height: 0, depth: 0, weight: 0, max_width: 0, max_height: 0, thickness: 0, max_weight: 0, separator: 0, stack_distance: 0, priority: 999, safety_margin: 0, cost_m3: 0, active: true });
  }

  editRow(rowIn: Easel): void {
    this.selected = rowIn;
    this.errorMessage = '';
    this.feedbackMessage = '';
    this.editorForm.reset({
      id: rowIn.id, name: rowIn.name || '', desc: rowIn.desc || '', type: Number(rowIn.type || 3), family: rowIn.family,
      sides: Number(rowIn.sides || 1), stacks: Number(rowIn.stacks || 1), width: Number(rowIn.width || 0), height: Number(rowIn.height || 0),
      depth: Number(rowIn.depth || 0), weight: Number(rowIn.weight || 0), max_width: Number(rowIn.max_width || 0), max_height: Number(rowIn.max_height || 0),
      thickness: Number(rowIn.thickness || 0), max_weight: Number(rowIn.max_weight || 0), separator: Number(rowIn.separator || 0),
      stack_distance: Number(rowIn.stack_distance || 0), priority: rowIn.priority === null || rowIn.priority === undefined ? 999 : Number(rowIn.priority),
      safety_margin: Number(rowIn.safety_margin || 0), cost_m3: Number(rowIn.cost_m3 || 0), active: rowIn.active === true || Number(rowIn.active) === 1
    });
  }

  saveRow(): void {
    if (this.saving) return;
    if (this.editorForm.invalid || !(Number(this.editorForm.value.width) >= 0) || !(Number(this.editorForm.value.height) >= 0) || !(Number(this.editorForm.value.depth) >= 0)) {
      this.errorMessage = 'Revisa el nombre, la familia y las dimensiones del caballete.';
      return;
    }
    const raw = this.editorForm.value;
    const numericFields = ['sides', 'stacks', 'width', 'height', 'depth', 'weight', 'max_width', 'max_height', 'thickness', 'max_weight', 'separator', 'stack_distance', 'priority', 'safety_margin', 'cost_m3'];
    const payload: any = Object.assign({}, this.selected || {}, raw, { name: (raw.name || '').trim(), desc: (raw.desc || '').trim(), type: Number(raw.type || 3), family: Number(raw.family), active: raw.active === true });
    numericFields.forEach(key => payload[key] = Number(raw[key] || 0));
    this.saving = true;
    this.errorMessage = '';
    this.feedbackMessage = '';
    const request = payload.id ? this.easelService.updateEasel(payload) : this.easelService.createEasel(payload);
    request.subscribe(data => {
      this.saving = false;
      if (!data) { this.errorMessage = 'No se ha podido guardar el caballete.'; return; }
      this.editRow(data);
      this.feedbackMessage = 'Caballete guardado correctamente.';
      this.loadRows();
    });
  }

  askDelete(rowIn: Easel): void { this.selected = rowIn; this.deleteModal = true; }
  confirmDelete(): void {
    if (!this.selected) return;
    this.easelService.deleteEasel(this.selected).subscribe(result => {
      this.deleteModal = false;
      if (result === null || result === undefined) { this.errorMessage = 'No se ha podido eliminar el caballete.'; return; }
      this.createNew(); this.feedbackMessage = 'Caballete eliminado correctamente.'; this.loadRows();
    });
  }

  getTypeName(idIn: any): string { const found = this.types.find(item => Number(item.id) === Number(idIn)); return found ? found.name : 'Sin tipo'; }
  getFamilyName(idIn: any): string { const found = this.families.find(item => Number(item.id) === Number(idIn)); return found ? found.name : 'Sin familia'; }
  availableFamilies(): EaselFamily[] { const typeId = Number(this.editorForm && this.editorForm.value.type || 3); return this.families.filter(item => Number(item.easel_type_id) === typeId); }
  physicalSize(item: Easel): string { return Number(item.width || 0) + ' × ' + Number(item.height || 0) + ' × ' + Number(item.depth || 0) + ' mm'; }
  capacitySize(item: Easel): string { return Number(item.max_width || 0) + ' × ' + Number(item.max_height || 0) + ' × ' + Number(item.thickness || 0) + ' mm'; }
  private defaultFamily(): any { const found = this.families.find(item => Number(item.easel_type_id) === 3); return found ? found.id : null; }
}
